import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { ArrowLeft, BarChart3 } from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const ESTADO_COLORS: Record<string, string> = {
  pendiente: '#f59e0b',
  aprobada: '#10b981',
  rechazada: '#ef4444',
  en_espera: '#8b5cf6',
};

function formatCurrency(value: number) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

export default function Analitica() {
  const navigate = useNavigate();

  // Compras por periodo
  const { data: comprasData } = useQuery({
    queryKey: ['analytics-compras-periodo'],
    queryFn: () => api.getComprasPorPeriodo({ agrupacion: 'mensual' }),
  });

  // Top proveedores
  const { data: proveedoresData } = useQuery({
    queryKey: ['analytics-proveedores'],
    queryFn: () => api.getComprasPorProveedor({ limit: '8' }),
  });

  // Facturas por estado
  const { data: estadoData } = useQuery({
    queryKey: ['analytics-facturas-estado'],
    queryFn: () => api.getFacturasPorEstado(),
  });

  const compras = comprasData?.data || [];
  const proveedores = proveedoresData?.data || [];
  const estados = estadoData?.data || [];

  // Totales
  const totalFacturas = estados.reduce((sum: number, e: any) => sum + e.total, 0);
  const totalMonto = estados.reduce((sum: number, e: any) => sum + e.monto_total, 0);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Analitica de Facturas</h1>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Facturas</p>
          <p className="text-2xl font-bold">{totalFacturas}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Monto Total</p>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalMonto)}</p>
        </div>
        {estados.map((e: any) => (
          <div key={e.estado} className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500 capitalize">{e.estado === 'en_espera' ? 'En Espera' : e.estado}</p>
            <p className="text-2xl font-bold" style={{ color: ESTADO_COLORS[e.estado] || '#6b7280' }}>
              {e.total}
            </p>
            <p className="text-xs text-gray-400">{formatCurrency(e.monto_total)}</p>
          </div>
        ))}
      </div>

      {/* Charts Row 1: Compras por periodo + Estado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-lg font-semibold mb-4">Facturacion por Mes</h3>
          {compras.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={compras}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodo" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                <Bar dataKey="total_monto" fill="#3b82f6" name="Monto" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">Sin datos</div>
          )}
        </div>

        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-lg font-semibold mb-4">Facturas por Estado</h3>
          {estados.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={estados.map((e: any) => ({
                    name: e.estado === 'en_espera' ? 'En Espera' : e.estado.charAt(0).toUpperCase() + e.estado.slice(1),
                    value: e.total,
                  }))}
                  cx="50%" cy="50%" outerRadius={100}
                  dataKey="value" label={({ name, value }) => `${name}: ${value}`}
                >
                  {estados.map((e: any, idx: number) => (
                    <Cell key={idx} fill={ESTADO_COLORS[e.estado] || COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">Sin datos</div>
          )}
        </div>
      </div>

      {/* Charts Row 2: Gasto por proveedor + Facturas por mes (count) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-lg font-semibold mb-4">Gasto por Proveedor</h3>
          {proveedores.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={proveedores.map((p: any) => ({
                name: (p.proveedor || 'Sin nombre').substring(0, 20),
                monto: p.total_monto,
                facturas: p.total_facturas,
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                <Bar dataKey="monto" fill="#8b5cf6" name="Monto Total" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">Sin datos</div>
          )}
        </div>

        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-lg font-semibold mb-4">Cantidad de Facturas por Mes</h3>
          {compras.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={compras}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodo" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="total_facturas" fill="#10b981" name="Facturas" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">Sin datos</div>
          )}
        </div>
      </div>
    </div>
  );
}
